/**
 * Events calendar
 */
import { createInitializeOnce, scrollToElementIfNecessary } from './utilities';
import { debounce } from 'lodash';

const initializeOnce = createInitializeOnce();


export function calendarFiltersOpen() {
    const dialog = document.querySelector('.events-filters-menu');
    const toggle = document.querySelector('.events-filters-toggle');
    if (!dialog || !toggle) {
        return
    }

    toggle.classList.add('open');
    dialog.classList.add('open');
    // On small screen sizes we use the native dialog element
    if (window.isSmall) {
        dialog.showModal();
    }
}

export function calendarFiltersClose() {
    const dialog = document.querySelector('.events-filters-menu');
    const toggle = document.querySelector('.events-filters-toggle');
    if (!dialog || !toggle) {
        return
    }

    toggle.classList.remove('open');
    dialog.classList.remove('open');
    dialog.close();
}

function toggleDialog({ target }) {
    if (!document.querySelector('.events-controls')) return;

    const dialog = document.querySelector('.events-filters-menu');
    const toggle = document.querySelector('.events-filters-toggle');

    // Click to directly open or close the filters menu
    if (target.closest('.events-filters-toggle, .events-filters-x') || target.matches('.events-filters-menu__close')) {
        dialog.classList.contains('open') ? calendarFiltersClose() : calendarFiltersOpen();
        return;
    }

    // Close when clicking outside while open
    if (toggle?.matches('.open') && !target.closest('.events-filters-menu')) {
        calendarFiltersClose();
    }
}

function handleToggleKeydown(event) {
    const { target, code } = event;

    if (code === 'Escape') {
        calendarFiltersClose();
        return;
    }

    if (target.closest('.events-toggle') && ['Enter', 'Space'].includes(code)) {
        event.preventDefault();
        target.click();
    }
}

// Special handling for the exhibition toggle button and mobile toggle buttons
function handleToggleButtonClick({ target }) {
    // Remove desktop and mobile exhibition toggle buttons when user deselects one of them
    if (target.closest('.events-toggle__exhibition')) {
        const formEl = target.closest('.events-form');
        formEl.querySelectorAll('.events-toggle__exhibition').forEach((element) => {
            element.remove();
        });
        formEl.requestSubmit();

        return;
    }

    const chip = target.closest('.events-toggle__mobile');
    if (!chip) {
        return;
    }

    // The toggle buttons on mobile are not actual form elements, so make sure that the corresponding input is toggled
    chip.classList.toggle('active');
    const toggleInput = document.querySelector(`.events-toggle input[value="${ chip.dataset.tagValue }"]`);
    toggleInput.checked = !toggleInput.checked;

    document.querySelector('form.events-form').requestSubmit();
    updateFilterMenu();
}

function updateFilterMenu() {
    // Make sure the corresponding tag toggle buttons on desktop and mobile have the same state
    document.querySelectorAll('.events-toggle__mobile:not(.events-toggle__exhibition)').forEach((element) => {
        const { tagValue } = element.dataset;
        const toggleInput = document.querySelector(`.events-toggle input[value="${ tagValue }"]`);

        if (toggleInput.checked) {
            element.classList.add('active');
        } else {
            element.classList.remove('active');
        }
    });

    // Check the "All" filters checkbox if no filters are selected
    const allFiltersButton = document.querySelector('.events-checkbox--all input');
    if (!allFiltersButton) {
        return;
    }

    allFiltersButton.checked = !document.querySelectorAll('.events-toggle input:checked, .events-toggle.active, .events-filters-menu label:not(.events-checkbox--all) input:checked').length;

    if (allFiltersButton.checked) {
        allFiltersButton.setAttribute('disabled', 'true');
    } else {
        allFiltersButton.removeAttribute('disabled');
    }

    // Count the number of selected filters and indicate it
    // Only count the three mobile toggle buttons on mobile
    const selectorQuery = '.events-filters-menu label:not(.events-checkbox--all) input:checked'
        + ((window.isSmall || window.isMedium) ? ', .events-toggle__mobile.active' : '');

    const selectedFilterMenuItems = document.querySelectorAll(selectorQuery).length;
    const filterButtonText = document.querySelector('.events-filters-toggle-text');

    document.querySelector('.events-filters-toggle')?.classList.toggle('selection', selectedFilterMenuItems);

    filterButtonText.innerHTML = selectedFilterMenuItems ? `Filters (${ selectedFilterMenuItems })` : 'Narrow <span>your</span> search';
}

function setupTodayExpandable(eventsForm) {
    const todaySection = document.querySelector('.events-today');

    if (!todaySection) return;

    const itemCount = todaySection.querySelectorAll('li').length;
    const moreButton = todaySection.querySelector('.events-today__more');
    moreButton.classList.toggle('hidden', (window.isSmall && itemCount <= 2) || (!window.isSmall && itemCount <= 4));

    eventsForm.addEventListener('click', ({ target }) => {
        if (!target.matches('.events-today__more')) return;

        document.querySelector('#today-events-list').classList.add('active');
        target.remove();
    });
}

function handleFilterClear(event) {
    const { target } = event;

    if (!target.matches('.events-checkbox--all') && !target.closest('.events-checkbox--all')) {
        return;
    }

    event.preventDefault();

    const allCheckbox = target.matches('input') ? target : target.querySelector('input');

    setTimeout(() => {
        allCheckbox.checked = true;
    }, 50);

    document.querySelectorAll('.events-toggle input[type="checkbox"], .events-toggle.active, .events-filters-menu ul label.checkbox:not(.events-checkbox--all) input').forEach((element) => {
        element.checked = false;
    });

    target.closest('.events-form').dispatchEvent(new Event('change'));
}

function handleFormChange({ target }) {
    updateFilterMenu();
    target.closest('form.events-form').requestSubmit();
}

// [] characters are URL encoded by default and are hard for humans to read
function prettifyUrl(event) {
    event.detail.url.href = event.detail.url.href.replace(/tags%5B%5D/g, "tags[]");
}

function handleTurboSubmitEnd({ target }) {
    if (!target.matches('.events-form')) {
        return;
    }

    // Scroll specifically to the events list so controls are directly below the primary nav
    scrollToElementIfNecessary(target.querySelector('.events-list'));
}

function trackClicks({ target }) {
    if (!useAnalytics || !target.closest('.events-form')) {
        return;
    }

    const toggle = target.closest('.events-toggle');
    const filterLabel = target.closest('.events-filter-label');

    if (toggle) {
        const value = toggle.dataset.tagValue || toggle.querySelector('input')?.value;

        dataLayer.push({
            event: 'buttons',
            label: 'calendar toggle button',
            value,
        });
    } else if (filterLabel) {
        dataLayer.push({
            event: 'buttons',
            label: 'calendar filter',
            value: filterLabel.querySelector('input')?.value,
        });
    } else if (target.closest('.events-checkbox--all')) {
        dataLayer.push({
            event: 'buttons',
            label: 'calendar filter',
            value: 'all',
        });
    } else if (target.closest('.events-start-over')) {
        dataLayer.push({
            event: 'buttons',
            label: 'calendar start over',
            value: 'true',
        });
    } else if (target.closest('.day-picker-submit')) {
        const value = document.querySelector('.day-picker-selected')?.innerText?.replace('Selected: ', '');
        dataLayer.push({
            event: 'buttons',
            label: 'calendar date submit',
            value,
        });
    } else if (target.closest('.event-calendar-similar ul li > a')) {
        dataLayer.push({
            event: 'buttons',
            label: 'calendar similar events link',
            value: target.closest('a')?.href,
        });
    }
}

const debouncedUpdateFilterMenu = debounce(updateFilterMenu, 250, { 'maxWait': 1000 });

export default function setupCalendar() {
    const el = document.querySelector('.events-form');
    if (!el) {
        return;
    }

    if (initializeOnce(el)) {
        setupTodayExpandable(el);
        el.addEventListener('click', toggleDialog);
        el.addEventListener('keydown', handleToggleKeydown);
        el.addEventListener('click', handleToggleButtonClick);
        el.addEventListener('click', handleFilterClear);
        el.addEventListener('click', trackClicks);
        el.addEventListener('turbo:before-fetch-request', prettifyUrl);
        el.addEventListener('turbo:submit-end', handleTurboSubmitEnd);
        el.addEventListener('change', handleFormChange);
        window.addEventListener('resize', debouncedUpdateFilterMenu);
        updateFilterMenu();

        // Make sure native dialog close also triggers needed class changes
        const dialog = document.querySelector('.events-filters-menu');
        const toggle = document.querySelector('.events-filters-toggle');
        if (dialog && toggle) {
            dialog.addEventListener('close', () => {
                toggle.classList.remove('open');
                dialog.classList.remove('open');
            });
        }
    }
}
